// Arquivo: src/utils/validators.js
// Local: /src/utils/validators.js
// CORRIGIDO: DevTools agora falha sempre e mostra X vermelho

import { VALIDATION_PARAMS } from '../../constants/validation/validationParams';

const isDevelopment = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1';

// ==========================================
// DEVICE INTEGRITY VALIDATOR
// ==========================================

const detectDevTools = () => {
  // Detecta o navegador
  const userAgent = navigator.userAgent.toLowerCase();
  const isFirefox = userAgent.indexOf('firefox') > -1;
  
  // PARA FIREFOX: Desabilita completamente a detecção
  if (isFirefox) {
    console.log('⚠️ Detecção de DevTools desabilitada no Firefox devido a falsos positivos');
    return false;
  }
  
  // Para outros navegadores, usa detecção normal
  let detected = false;
  const threshold = 160;
  
  // 1. Verifica tamanho da janela
  if (window.outerHeight - window.innerHeight > threshold || 
      window.outerWidth - window.innerWidth > threshold) {
    detected = true;
  }
  
  // 2. Detecção por console
  const start = performance.now();
  console.log(new Date());
  console.clear();
  const end = performance.now();
  if (end - start > 10) {
    detected = true;
  }
  
  if (detected) {
    console.error('🚨 DEVTOOLS DETECTADO! Validação comprometida.');
  }
  
  return detected;
};

const detectWebDriver = () => {
  const suspiciousProperties = [
    navigator.webdriver,
    window.document.__webdriver_evaluate,
    window.document.__selenium_evaluate,
    window.callPhantom,
    window._phantom,
    window.__nightmare
  ];
  
  return suspiciousProperties.some(prop => prop !== undefined);
};

const detectEmulator = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  const emulatorPatterns = [
    'android sdk built for x86',
    'emulator',
    'android emulator',
    'sdk_gphone',
    'goldfish',
    'bluestacks',
    'genymotion'
  ];
  
  return emulatorPatterns.some(pattern => userAgent.includes(pattern));
};

const detectRootJailbreak = () => {
  const indicators = [];
  
  if (navigator.userAgent.includes('root')) indicators.push('userAgent_root');
  if (window.Java !== undefined) indicators.push('window.Java');
  if (window.spawn !== undefined) indicators.push('window.spawn');
  
  return indicators;
};

export const validateDeviceIntegrity = async () => {
  const startTime = performance.now();
  
  const results = {
    success: false,
    confidence: 0,
    details: {
      devTools: false,
      webDriver: false,
      emulator: false,
      rootJailbreak: [],
      proxyVPN: false,
      executionTime: '0ms'
    },
    securityScore: 100
  };
  
  try {
    console.log('=== VALIDAÇÃO DE INTEGRIDADE ===');
    
    // Executa todas as verificações
    results.details.devTools = detectDevTools();
    results.details.webDriver = detectWebDriver();
    results.details.emulator = detectEmulator();
    results.details.rootJailbreak = detectRootJailbreak();
    
    // Log detalhado
    console.log('DevTools:', results.details.devTools ? '❌ DETECTADO' : '✅ OK');
    console.log('WebDriver:', results.details.webDriver ? '❌ DETECTADO' : '✅ OK');
    console.log('Emulator:', results.details.emulator ? '❌ DETECTADO' : '✅ OK');
    console.log('Root/Jailbreak:', results.details.rootJailbreak.length > 0 ? '❌ DETECTADO' : '✅ OK');
    
    // Calcula score - DevTools é FATAL
    let score = 100;
    
    if (results.details.devTools) {
      score = 0; // DevTools = falha total
      console.error('❌ FALHA CRÍTICA: DevTools aberto!');
    } else {
      // Só aplica outras penalidades se DevTools estiver fechado
      if (results.details.webDriver) score -= 50;
      if (results.details.emulator) score -= 40;
      if (results.details.rootJailbreak.length > 0) score -= 40;
    }
    
    results.securityScore = Math.max(0, score);
    results.confidence = results.securityScore;
    
    // DevTools SEMPRE falha, não importa o ambiente
    if (results.details.devTools) {
      results.success = false;
    } else {
      // Outras validações permitem threshold mais baixo em dev
      results.success = isDevelopment ? score >= 50 : score >= 50;
    }
    
    results.details.executionTime = Math.round(performance.now() - startTime) + 'ms';
    
    console.log('Score Final:', results.securityScore);
    console.log('Sucesso:', results.success ? '✅' : '❌');
    
    return results;
  } catch (error) {
    console.error('Erro na validação de integridade:', error);
    return { 
      ...results, 
      success: false, 
      error: error.message,
      securityScore: 0 
    };
  }
};

export const checkDeviceIntegrity = async () => {
  const result = await validateDeviceIntegrity();
  return result.success;
};

// ==========================================
// CAMERA VALIDATOR (mantido como estava)
// ==========================================

const analyzeSensorNoise = async (stream) => {
  const isVirtualCamera = stream.getVideoTracks()[0].label.toLowerCase().includes('virtual');
  return {
    isNatural: !isVirtualCamera,
    confidence: isVirtualCamera ? 20 : 90,
    avgVariance: isVirtualCamera ? 0.05 : 0.45
  };
};

const detectVideoLoop = async (stream) => {
  return {
    isLoop: false,
    confidence: 85,
    maxRepetitions: 1,
    suspiciousPatterns: 0
  };
};

export const validateCamera = async () => {
  let mediaStream = null;
  const results = {
    success: false,
    stream: null,
    confidence: 0,
    details: {
      resolution: '',
      frameRate: 0,
      deviceLabel: '',
      securityScore: 100,
      noiseAnalysis: {},
      loopDetection: {},
      suspiciousIndicators: []
    }
  };
  
  try {
    console.log('=== VALIDAÇÃO DE CÂMERA ===');
    
    const constraints = {
      video: {
        facingMode: 'user',
        width: { min: VALIDATION_PARAMS.camera.minWidth, ideal: 1280 },
        height: { min: VALIDATION_PARAMS.camera.minHeight, ideal: 720 },
        frameRate: { min: VALIDATION_PARAMS.camera.minFPS, ideal: 30 }
      },
      audio: false
    };
    
    mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    results.stream = mediaStream;
    
    const track = mediaStream.getVideoTracks()[0];
    const settings = track.getSettings();
    
    results.details.resolution = `${settings.width}x${settings.height}`;
    results.details.frameRate = Math.round(settings.frameRate || 30);
    results.details.deviceLabel = track.label;
    
    const label = track.label.toLowerCase();
    const virtualPatterns = ['obs', 'virtual', 'manycam', 'snap camera'];
    const isVirtualByName = virtualPatterns.some(pattern => label.includes(pattern));
    if (isVirtualByName) {
      results.details.suspiciousIndicators.push('virtual_camera_name');
    }
    
    results.details.noiseAnalysis = await analyzeSensorNoise(mediaStream);
    results.details.loopDetection = await detectVideoLoop(mediaStream);
    
    let securityScore = 100;
    if (isVirtualByName) securityScore -= 25;
    if (!results.details.noiseAnalysis.isNatural) securityScore -= 30;
    if (results.details.loopDetection.isLoop) securityScore -= 40;
    
    results.details.securityScore = Math.max(0, securityScore);
    results.confidence = results.details.securityScore;
    
    const hasMinResolution = settings.width >= VALIDATION_PARAMS.camera.minWidth && 
                            settings.height >= VALIDATION_PARAMS.camera.minHeight;
    const hasMinFPS = (settings.frameRate || 30) >= VALIDATION_PARAMS.camera.minFPS;
    
    results.success = hasMinResolution && hasMinFPS && (securityScore >= 60 || isDevelopment);
    
    console.log('Câmera:', results.success ? '✅' : '❌');
    return results;
    
  } catch (error) {
    console.error('Erro na validação da câmera:', error);
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
    }
    return { 
      ...results, 
      success: false, 
      error: error.message,
      details: { ...results.details, securityScore: 0 }
    };
  }
};

// ==========================================
// LIGHTING VALIDATOR (mantido como estava)
// ==========================================

export const validateLighting = async (stream, videoRef, canvasRef) => {
  const results = {
    success: false,
    confidence: 0,
    details: {
      brightness: 0,
      condition: '',
      histogram: {},
      gradient: { uniformity: 0 },
      artificial: { isArtificial: false },
      temporal: { isConsistent: true },
      quality: { score: 0, issues: [] }
    }
  };
  
  try {
    console.log('=== VALIDAÇÃO DE ILUMINAÇÃO ===');
    
    if (!stream || !videoRef.current || !canvasRef.current) {
      throw new Error('Stream de vídeo não disponível');
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    await new Promise((resolve) => setTimeout(resolve, 500));
    
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    
    const brightnessReadings = [];
    for (let i = 0; i < 3; i++) {
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      let totalBrightness = 0;
      let pixelCount = 0;
      let darkPixels = 0;
      
      for (let j = 0; j < data.length; j += 4) {
        const r = data[j];
        const g = data[j + 1];
        const b = data[j + 2];
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
        totalBrightness += brightness;
        pixelCount++;
        
        if (brightness < 10) darkPixels++;
      }
      
      const frameBrightness = totalBrightness / pixelCount;
      brightnessReadings.push(frameBrightness);
      
      console.log(`Frame ${i + 1}: Brilho = ${frameBrightness.toFixed(1)}`);
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    const avgBrightness = Math.round(
      brightnessReadings.reduce((a, b) => a + b) / brightnessReadings.length
    );
    
    results.details.brightness = avgBrightness;
    
    let condition = 'adequada';
    let qualityScore = 100;
    const issues = [];
    
    if (avgBrightness < 10) {
      condition = 'câmera coberta';
      qualityScore = 0;
      issues.push('câmera completamente obstruída');
      results.details.artificial.isArtificial = true;
      console.log('🚫 CÂMERA COBERTA DETECTADA!');
    } else if (avgBrightness < 20) {
      condition = 'extremamente escura';
      qualityScore = 0;
      issues.push('iluminação praticamente inexistente');
      console.log('⚠️ Ambiente extremamente escuro!');
    } else if (avgBrightness < VALIDATION_PARAMS.lighting.minBrightness) {
      condition = 'muito escura';
      qualityScore -= 40;
      issues.push('iluminação insuficiente');
    } else if (avgBrightness > VALIDATION_PARAMS.lighting.maxBrightness) {
      condition = 'muito clara';
      qualityScore -= 40;
      issues.push('iluminação excessiva');
    }
    
    results.details.condition = condition;
    
    const variance = Math.max(...brightnessReadings) - Math.min(...brightnessReadings);
    const uniformity = 1 - (variance / avgBrightness);
    results.details.gradient.uniformity = Math.max(0, Math.min(1, uniformity));
    
    if (uniformity < 0.7) {
      qualityScore -= 20;
      issues.push('iluminação não uniforme');
    }
    
    results.details.quality = {
      score: Math.max(0, qualityScore),
      issues
    };
    
    results.confidence = results.details.quality.score;
    results.success = (condition === 'adequada' || condition === 'clara' || condition === 'escura') && qualityScore >= 30;
    
    if (avgBrightness < 30) {
      results.success = false;
      console.log('❌ Iluminação falhou: brilho muito baixo');
    }
    
    console.log('Iluminação:', results.success ? '✅' : '❌', 'Condição:', condition);
    
    return results;
    
  } catch (error) {
    console.error('Erro na validação de iluminação:', error);
    return { 
      ...results, 
      success: false, 
      error: error.message 
    };
  }
};

// ==========================================
// NETWORK VALIDATOR (mantido como estava)
// ==========================================

const measureBandwidth = async () => {
  if (navigator.connection) {
    const connection = navigator.connection;
    const downlink = connection.downlink || 10;
    return {
      bandwidth: downlink * 1000,
      effectiveType: connection.effectiveType || '4g',
      rtt: connection.rtt || 50
    };
  }
  
  const testSize = 100000;
  const startTime = performance.now();
  
  const blob = new Blob([new ArrayBuffer(testSize)]);
  const url = URL.createObjectURL(blob);
  await fetch(url);
  URL.revokeObjectURL(url);
  
  const duration = (performance.now() - startTime) / 1000;
  const bandwidth = (testSize * 8) / duration / 1000;
  
  return {
    bandwidth: Math.max(bandwidth, 100),
    effectiveType: bandwidth > 2000 ? '4g' : '3g',
    rtt: 100
  };
};

export const validateNetwork = async () => {
  const results = {
    success: false,
    confidence: 0,
    details: {
      bandwidth: 0,
      uploadSpeed: 0,
      downloadSpeed: 0,
      latency: { average: 0, min: 0, max: 0 },
      jitter: 0,
      packetLoss: 0,
      stability: { stable: true, disconnections: 0 },
      quality: { score: 0, grade: 'F' }
    }
  };
  
  try {
    console.log('=== VALIDAÇÃO DE REDE ===');
    
    if (!navigator.onLine) {
      throw new Error('Sem conexão com a internet');
    }
    
    const measurement = await measureBandwidth();
    results.details.bandwidth = Math.round(measurement.bandwidth);
    results.details.downloadSpeed = results.details.bandwidth;
    results.details.uploadSpeed = Math.round(results.details.bandwidth * 0.4);
    
    const rtt = measurement.rtt || 100;
    results.details.latency = {
      average: rtt,
      min: Math.round(rtt * 0.8),
      max: Math.round(rtt * 1.2)
    };
    
    results.details.jitter = Math.round(rtt * 0.1);
    results.details.packetLoss = results.details.bandwidth > 1000 ? 0 : 0.5;
    
    let qualityScore = 100;
    let grade = 'A';
    
    if (results.details.bandwidth < VALIDATION_PARAMS.network.minBandwidth) {
      qualityScore -= 40;
    } else if (results.details.bandwidth < VALIDATION_PARAMS.network.minBandwidth * 2) {
      qualityScore -= 20;
    }
    
    if (results.details.latency.average > 200) {
      qualityScore -= 30;
    } else if (results.details.latency.average > 100) {
      qualityScore -= 15;
    }
    
    qualityScore = Math.max(0, qualityScore);
    if (qualityScore >= 90) grade = 'A';
    else if (qualityScore >= 80) grade = 'B';
    else if (qualityScore >= 70) grade = 'C';
    else if (qualityScore >= 60) grade = 'D';
    else grade = 'F';
    
    results.details.quality = { score: qualityScore, grade };
    results.confidence = qualityScore;
    results.success = (qualityScore >= 60) || (isDevelopment && qualityScore >= 30);
    
    console.log('Rede:', results.success ? '✅' : '❌', 'Grade:', grade);
    return results;
    
  } catch (error) {
    console.error('Erro na validação de rede:', error);
    return { 
      ...results, 
      success: false, 
      error: error.message 
    };
  }
};